import { storeToRefs } from "pinia";
import axios from "axios";

import { useEnvironmentStore } from "@/stores/environment";
import { useAuthenticationStore } from "@/stores/authentication";
import { useUserRoleStore } from "@/stores/userRole";
import { useOrganisationsStore } from "@/stores/organisations";
import { useLogSourcesStore } from "@/stores/logSources";
import { useTokenResetTimestampStore } from "@/stores/tokenResetTimestamp";
import { useGrokPatternsStore } from "@/stores/grokPatterns";
import { useProductsStore } from "@/stores/products";

export function useAuthentication() {
  const environmentStore = useEnvironmentStore();

  const authenticationStore = useAuthenticationStore();
  const { authentication, decodedAuthentication } =
    storeToRefs(authenticationStore);

  const organisationsStore = useOrganisationsStore();

  const tokenResetTimestampStore = useTokenResetTimestampStore();
  const { tokenResetTimestamp } = storeToRefs(tokenResetTimestampStore);

  const userRoleStore = useUserRoleStore();

  const logSourcesStore = useLogSourcesStore();

  const grokPatternsStore = useGrokPatternsStore();

  const productsStore = useProductsStore();

  axios.defaults.headers.common["Authorization"] =
    `Bearer ${authentication.value ?? ""}`;

  const authenticateUser = async () => {
    if (authentication.value !== null) {
      try {
        const { data } = await axios.get(
          `user/authenticate/${decodedAuthentication.value.user_id}`,
        );

        if (!data.success) {
          throw new Error(
            "Authentication failed: API returned a non-success response.",
          );
        }
      } catch (error) {
        const message = "Unauthorized token. Please login again.";
        logout(message);

        console.error("Authentication error:", error);
      }
    }
  };

  const logout = async (message = "") => {
    try {
      await axios.post(
        `${process.env.VUE_APP_AUTH_API_BASE_URL}/users/${decodedAuthentication.value.user_id}/logout`,
      );
    } catch (error) {
      console.error("Logout failed:", error);
    }

    unsetAllStates();
    redirectToLogin(message);
  };

  const unsetAllStates = () => {
    environmentStore.unset();
    authenticationStore.unset();
    userRoleStore.unset();
    organisationsStore.unsetOrganisations();
    organisationsStore.unsetSelectedOrganisation();
    logSourcesStore.unset();
    grokPatternsStore.unset();
    productsStore.unset();
    tokenResetTimestampStore.unset();
  };

  const redirectToLogin = (message = "") => {
    let redirectUrl = encodeURIComponent(
      `${process.env.VUE_APP_BASE_URL}/validation`,
    );

    let href = `${process.env.VUE_APP_AUTH_BASE_URL}/login/?redirect_url=${redirectUrl}&logout=true`;

    message = message.trim();
    if (message !== "") {
      if (message === "unauthorized-access") {
        message = "Your session has expired. Please login again to continue.";
      }

      href += "&message=" + encodeURIComponent(message);
    }

    window.location.href = href;
  };

  const refreshToken = async () => {
    if (
      typeof authentication.value === "undefined" ||
      authentication.value === null
    ) {
      const message = "Unauthorized token. Please login again.";
      logout(message);
      return;
    }

    const date = new Date();
    if (date.getTime() < tokenResetTimestamp.value) {
      return;
    }

    const params = {
      id: decodedAuthentication.value.user_id,
      access_token: authentication.value,
      refresh_token: decodedAuthentication.value.jwt_refresh_token,
    };

    await axios
      .post(
        `${process.env.VUE_APP_AUTH_API_BASE_URL}/users/token/refresh`,
        params,
      )
      .then(({ data }) => {
        if (data.success) {
          authenticationStore.set(data.user.access_token);
          const millisecs = 1000 * 60 * 10;
          tokenResetTimestampStore.set(date.getTime() + millisecs);
        } else {
          logout(data.message);
        }
      })
      .catch(() => {
        logout("Unauthorized token. Please login again.");
      });
  };

  return {
    authenticateUser,
    refreshToken,
    logout,
    redirectToLogin,
  };
}
